import { ActionError, Button, toast } from '@westhetics/ui'
import { useId, useState, type FormEvent } from 'react'
import { Navigate } from 'react-router'
import { errorMessage, fieldErrors } from '@/lib/errors'
import { useSession } from '@/lib/session'
import { routes } from '@/routes'
import { isAcceptablePassword, useChangePassword } from './api'
import {
  AuthLayout,
  AuthSkeleton,
  FieldNote,
  PasswordLabel,
  authInput,
  authLink,
} from './auth-layout'

/**
 * A troca obrigatória da senha temporária (docs/areas/autenticacao.md, "Troca de senha
 * obrigatória"; RT-04, RN-AUT-05, RN-AUT-06), no leiaute do login (P-AUT-12). Só chega aqui quem
 * entrou com a senha temporária, pela senha: o token traz a marca, a pessoa informa a atual e
 * define a nova, e até a troca a API recusa todo o resto.
 *
 * Entrar com código não passa por aqui, nem como oferta (T48): é um login rápido, sem senha. Criar
 * ou trocar a senha por vontade própria é em Sua conta › Senha.
 */

const MESSAGES = {
  current: 'Informe a senha atual.',
  weak: 'A senha precisa de pelo menos 8 caracteres, com letras e números.',
  same: 'A nova senha precisa ser diferente da atual.',
} as const

export function NewPasswordPage() {
  const session = useSession()
  const change = useChangePassword()
  const currentId = useId()
  const nextId = useId()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [showCurrent, setShowCurrent] = useState(false)
  const [showNext, setShowNext] = useState(false)
  const [touched, setTouched] = useState({ current: false, next: false })
  // A recusa da API vale até a pessoa mexer no campo.
  const [refused, setRefused] = useState<Record<string, string>>({})
  // Enquanto salva, a sessão muda por baixo (o token perde a marca): a tela fica como estava.
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  // Senha salva: a tela segue adiante quando a sessão já reflete o token novo. A porta de entrada
  // decide o destino (escolha de clínica, implantação ou painel), ou o login, se a sessão caiu.
  if (saved && session.status !== 'password_required') return <Navigate to="/" replace />

  if (!saving) {
    if (session.status === 'loading') {
      return (
        <AuthLayout title="Nova senha">
          <AuthSkeleton />
        </AuthLayout>
      )
    }
    if (session.status === 'signed_out') return <Navigate to={routes.login} replace />
    // Sem a marca de troca obrigatória não há o que fazer aqui: a porta de entrada decide o destino.
    if (session.status !== 'password_required') return <Navigate to="/" replace />
  }

  const errors = {
    current: current === '' ? MESSAGES.current : null,
    next: !isAcceptablePassword(next) ? MESSAGES.weak : next === current ? MESSAGES.same : null,
  }
  const currentError = refused.currentPassword ?? (touched.current ? errors.current : null)
  const nextError = refused.newPassword ?? (touched.next ? errors.next : null)
  const canSubmit = !errors.current && !errors.next
  const serverFields = fieldErrors(change.error)
  // Erro que não é de nenhum dos dois campos (limite de tentativas, falha de rede) aparece inteiro.
  const generalError =
    change.isError && !serverFields.currentPassword && !serverFields.newPassword
      ? errorMessage(change.error)
      : null

  async function submit(event: FormEvent) {
    event.preventDefault()
    setTouched({ current: true, next: true })
    if (!canSubmit || change.isPending) return
    setSaving(true)
    try {
      await change.mutateAsync({ currentPassword: current, newPassword: next })
    } catch (error) {
      setRefused(fieldErrors(error))
      setSaving(false)
      return
    }
    toast('Senha salva.')
    setSaved(true)
  }

  return (
    <AuthLayout title="Criar sua senha" onSubmit={submit}>
      <p className="font-body text-secondary mt-3 text-[13px] leading-[1.6] font-light">
        A senha que você recebeu é temporária. Defina uma nova para continuar.
      </p>

      <PasswordLabel
        htmlFor={currentId}
        shown={showCurrent}
        onToggle={() => setShowCurrent((value) => !value)}
        toggleLabel={showCurrent ? 'Ocultar a senha atual' : 'Mostrar a senha atual'}
        className="mt-8"
      >
        Senha atual
      </PasswordLabel>
      <input
        id={currentId}
        type={showCurrent ? 'text' : 'password'}
        autoComplete="current-password"
        value={current}
        aria-invalid={currentError ? true : undefined}
        aria-describedby={currentError ? `${currentId}-error` : undefined}
        onChange={(event) => {
          setCurrent(event.target.value)
          setRefused(({ currentPassword: _drop, ...rest }) => rest)
        }}
        onBlur={() => setTouched((state) => ({ ...state, current: true }))}
        className={authInput}
      />
      <FieldNote id={`${currentId}-error`} error={currentError} />

      <PasswordLabel
        htmlFor={nextId}
        shown={showNext}
        onToggle={() => setShowNext((value) => !value)}
        toggleLabel={showNext ? 'Ocultar a nova senha' : 'Mostrar a nova senha'}
        className="mt-6"
      >
        Nova senha
      </PasswordLabel>
      <input
        id={nextId}
        type={showNext ? 'text' : 'password'}
        autoComplete="new-password"
        value={next}
        aria-invalid={nextError ? true : undefined}
        aria-describedby={`${nextId}-hint`}
        onChange={(event) => {
          setNext(event.target.value)
          setRefused(({ newPassword: _drop, ...rest }) => rest)
        }}
        onBlur={() => setTouched((state) => ({ ...state, next: true }))}
        className={authInput}
      />
      <FieldNote
        id={`${nextId}-hint`}
        error={nextError}
        hint="Pelo menos 8 caracteres, com letras e números."
      />

      {generalError ? (
        <ActionError
          className="mt-6"
          title="Não foi possível salvar a senha"
          description={generalError}
        />
      ) : null}

      <Button
        type="submit"
        size="lg"
        className="mt-7"
        loading={change.isPending || saved}
        loadingLabel="Salvando…"
      >
        Salvar e entrar
      </Button>
      <button type="button" onClick={() => void session.signOut()} className={`${authLink} mt-5`}>
        Sair e entrar com outra conta
      </button>
    </AuthLayout>
  )
}
